import React, { useMemo } from 'react';
import useBuildingAutocomplete from '@/features/buildings/hooks/useBuildingAutocomplete';
import { cn } from '@/lib/cn';
import BuildingSuggestions from './components/BuildingSuggestions';
import { NycAutocompleteSuggestion } from '@repo/api';
import SelectedNeighborhoodChips from './components/SelectedNeighborhoodChips';
import NeighborhoodGroupedList from './components/NeighborhoodGroupedList';
import { useStore } from '@/providers/StoreProvider';
import { Chip, ListCellSkeleton } from '@/components/ui';

export interface LocationResultsProps extends React.HTMLAttributes<HTMLDivElement> {
  searchQuery: string;
  variant?: 'desktop' | 'mobile';
  showBuildingSuggestions?: boolean;
  selectedNeighborhoodIds?: string[];
  onToggleNeighborhood?: (id: string) => void;
  onToggleNeighborhoodWithChildren?: (id: string) => void;
  onToggleBorough?: (borough: string) => void;
  onSelectResult?: () => void;
}

export default function LocationResults({
  searchQuery,
  className,
  showBuildingSuggestions = true,
  selectedNeighborhoodIds,
  onToggleNeighborhood,
  onToggleNeighborhoodWithChildren,
  onToggleBorough,
  onSelectResult,
  ...rest
}: LocationResultsProps) {
  const { suggestions, isLoading } = useBuildingAutocomplete(searchQuery, {
    enabled: showBuildingSuggestions,
    useNycApi: showBuildingSuggestions,
  });
  const neighborhoodsByBorough = useStore(s => s.neighborhoods.neighborhoodsByBorough);
  const filterByQuery = useStore(s => s.neighborhoods.filterByQuery);

  const filteredNeighborhoods = useMemo(() => {
    const q = searchQuery?.trim();
    return q ? filterByQuery(q) : neighborhoodsByBorough || {};
  }, [searchQuery, filterByQuery, neighborhoodsByBorough]);

  const hasResults = useMemo(() => {
    const hasBuildings = showBuildingSuggestions && suggestions && suggestions.length > 0;
    const hasNeighborhoods = Object.keys(filteredNeighborhoods).length > 0;
    return hasBuildings || hasNeighborhoods;
  }, [showBuildingSuggestions, suggestions, filteredNeighborhoods]);

  if (searchQuery.length < 1) {
    return null;
  }

  return (
    <div className={cn('flex flex-col gap-1', className)} {...rest}>
      {showBuildingSuggestions &&
        searchQuery.length > 3 &&
        (isLoading ? (
          <div className="">
            {[1, 2, 3].map(i => (
              <ListCellSkeleton
                key={i}
                className="rounded-none shadow-none border-b border-tertiary"
              />
            ))}
          </div>
        ) : (
          <BuildingSuggestions suggestions={suggestions as NycAutocompleteSuggestion[]} />
        ))}
      <NeighborhoodGroupedList
        searchQuery={searchQuery}
        {...(selectedNeighborhoodIds
          ? {
              selectedNeighborhoodIds,
              onToggleNeighborhood,
              onToggleNeighborhoodWithChildren,
              onToggleBorough,
              onSelectResult,
            }
          : {})}
      />
      {!hasResults && !(showBuildingSuggestions && isLoading) && (
        <div className="py-8 px-4 text-center">
          <p className="text-body-2 text-secondary">No results found</p>
        </div>
      )}
    </div>
  );
}
